'use client';

import Link, { type LinkProps } from 'next/link';
import { useRouter } from 'next/navigation';
import {
  type AnchorHTMLAttributes,
  type ComponentProps,
  useCallback,
} from 'react';

type AppLinkProps = Omit<ComponentProps<typeof Link>, 'prefetch'> & {
  /** primary = viewport prefetch; navigation = prefetch on hover/focus only */
  intent?: 'primary' | 'navigation';
  href: LinkProps['href'];
};

function isExternalHref(href: LinkProps['href']): boolean {
  if (typeof href !== 'string') {
    return false;
  }

  return (
    href.startsWith('http://') ||
    href.startsWith('https://') ||
    href.startsWith('//')
  );
}

function hrefToPrefetchPath(href: LinkProps['href']): string | null {
  if (typeof href === 'string') {
    return isExternalHref(href) ? null : href;
  }

  if ('pathname' in href && href.pathname) {
    const query =
      href.query && typeof href.query === 'object'
        ? `?${new URLSearchParams(
            href.query as Record<string, string>
          ).toString()}`
        : '';
    return `${href.pathname}${query}`;
  }

  return null;
}

export default function AppLink({
  intent = 'navigation',
  href,
  onMouseEnter,
  onFocus,
  ...props
}: AppLinkProps) {
  const router = useRouter();

  const prefetchRoute = useCallback(() => {
    if (intent !== 'navigation') {
      return;
    }

    const path = hrefToPrefetchPath(href);
    if (path) {
      router.prefetch(path);
    }
  }, [href, intent, router]);

  if (typeof href === 'string' && isExternalHref(href)) {
    const anchorProps = props as AnchorHTMLAttributes<HTMLAnchorElement>;
    return <a href={href} {...anchorProps} />;
  }

  return (
    <Link
      href={href}
      prefetch={intent === 'primary'}
      onMouseEnter={(event) => {
        prefetchRoute();
        onMouseEnter?.(event);
      }}
      onFocus={(event) => {
        prefetchRoute();
        onFocus?.(event);
      }}
      {...props}
    />
  );
}
